import { useEffect, useMemo } from "react";

import { shouldOptionBeShown } from "../../../helpers/conditions.ts";
import { useVehicleSelection } from "../../../providers/VehicleSelectionContext.tsx";
import {
  ComplexBundle,
  Product as ProductType,
} from "../../../types/Product.ts";
import DropdownLarge from "../../elements/DropdownLarge.tsx";

interface Props {
  step: ComplexBundle.Step;
  stepIndex: number;
  entry: ComplexBundle.Entry;
  entryIndex: number;
}

export const ProductDropdown = ({ stepIndex, entry, entryIndex }: Props) => {
  const {
    selectedOptions,
    handleOptionChange,
    selectedGvmUpgrade,
    validationErrors,
  } = useVehicleSelection();

  const hasError = validationErrors.some(
    (err) => err.stepIndex === stepIndex && err.entryIndex === entryIndex,
  );
  const options = useMemo(() => {
    if (!selectedGvmUpgrade) {
      return [];
    }
    return Array.from(entry.products.entries())
      .filter(([, option]) => shouldOptionBeShown(option, selectedOptions))
      .map(([optionIndex, option]): ProductType => {
        return {
          id: optionIndex,
          value: optionIndex,
          name: option.option_name,
          price: option.hide_price
            ? null
            : Number(option.product_option.adjusted_price),
          image_url: option.product_option.image_url,
          attributes: null,
          entry: entry,
          option: option,
          optionKey: `${stepIndex}-${entryIndex}-${optionIndex}`,
        };
      });
  }, [
    entry.products,
    entryIndex,
    selectedGvmUpgrade,
    selectedOptions,
    stepIndex,
  ]);

  const entryKey = `${stepIndex}-${entryIndex}`;

  // A selection whose option is no longer visible must be cleared, or it would still be
  // submitted with the cart payload and fail the server price check.
  useEffect(() => {
    const currentlySelected = selectedOptions[entryKey] ?? null;

    if (currentlySelected === null) {
      return;
    }

    if (!options.find((opt) => opt.value === currentlySelected)) {
      handleOptionChange(stepIndex, entryIndex, null);
    }
  }, [
    handleOptionChange,
    entryIndex,
    options,
    selectedOptions,
    stepIndex,
    entryKey,
  ]);

  return (
    <DropdownLarge
      options={options}
      label={entry.entry_name}
      selected={(selectedOptions[entryKey] as number | undefined) ?? null}
      required={!!entry.entry_required}
      onChange={(value) => {
        handleOptionChange(
          stepIndex,
          entryIndex,
          value === null ? null : Number(value),
        );
      }}
      error={hasError}
    />
  );
};
